import { compact } from "lodash";
import { OrganizationTalentIndicatorFragment } from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import { currentOrganizationVar } from "@cache/cache";
import { assertEdgesNonNull } from "@helpers/helpers";

import {
  ProgramFormCoreValueReviewConfig,
  ProgramFormGoalReviewConfig,
  ProgramFormRoleBasedReviewConfig,
  ProgramFormTalentIndicatorReviewConfig,
} from "../types";

// The one-line summaries under each row of questions Topicflow adds, saying
// what reviewers will be asked.

const scaleRangeText = ({
  assessmentScale,
  assessmentScaleStartsAt,
}: {
  assessmentScale: number;
  assessmentScaleStartsAt: number;
}) =>
  `${assessmentScaleStartsAt}–${assessmentScaleStartsAt + assessmentScale - 1}`;

export const useCoreValues = () => {
  const organization = currentOrganizationVar();
  return organization.coreValues
    ? assertEdgesNonNull(organization.coreValues)
    : [];
};

export const useCoreValuesSummary = (
  config: ProgramFormCoreValueReviewConfig,
) => {
  const coreValues = useCoreValues();
  return `${coreValues.length} ${
    coreValues.length === 1 ? "value" : "values"
  }, each rated ${scaleRangeText(config)}, plus an overall rating.`;
};

export const useGoalsSummary = (config: ProgramFormGoalReviewConfig) => {
  const label = useLabel();
  return `Each ${label("goal")} in the ${label(
    "review",
  )} period, rated ${scaleRangeText(config)}, plus an overall rating.`;
};

export const useRolesSummary = (config: ProgramFormRoleBasedReviewConfig) => {
  const label = useLabel();
  return `Each ${label("competency")} of their role${
    config.enabledForNextRole ? " and next role" : ""
  }, rated ${scaleRangeText(config)}.`;
};

// What a talent indicator is called in the review: its question title, then
// its custom name, then its name, as the backend titles the question.
export const talentIndicatorTitle = (
  talentIndicator: Pick<
    OrganizationTalentIndicatorFragment,
    "questionTitle" | "customName" | "name"
  >,
) =>
  talentIndicator.questionTitle?.trim() ||
  talentIndicator.customName?.trim() ||
  talentIndicator.name;

// Names the indicators chosen in the sidebar, in its order. Before any are
// chosen, names all of them, as switching the row on adds them all.
export const talentIndicatorsSummary = (
  config: ProgramFormTalentIndicatorReviewConfig,
  applicableTalentIndicators: OrganizationTalentIndicatorFragment[],
) => {
  const talentIndicators =
    config.talentIndicators.length === 0
      ? applicableTalentIndicators
      : compact(
          config.talentIndicators.map(({ talentIndicatorId }) =>
            applicableTalentIndicators.find(
              ({ id }) => id === talentIndicatorId,
            ),
          ),
        );
  return `${talentIndicators.map(talentIndicatorTitle).join(", ")}.`;
};
